import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuthStore, useIsPlatform } from '@/store/auth';
import { useThemeStore } from '@/store/theme';
import { onUnauthorized } from '@/lib/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageLoader } from '@/components/ui/States';
import { LandingPage } from '@/pages/LandingPage';
import { AuthPage } from '@/pages/AuthPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { HargaPage } from '@/pages/HargaPage';
import { FiturPage } from '@/pages/FiturPage';
import { KontakPage } from '@/pages/KontakPage';
import { DokumentasiPage } from '@/pages/DokumentasiPage';
import { PaymentSuccessPage } from '@/pages/PaymentSuccessPage';
import { VerifikasiEmailPage } from '@/pages/VerifikasiEmailPage';
import { PeriksaEmailPage } from '@/pages/PeriksaEmailPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { JobListPage } from '@/pages/JobListPage';
import { JobCreatePage } from '@/pages/JobCreatePage';
import { JobDetailPage } from '@/pages/JobDetailPage';
import { RunDetailPage } from '@/pages/RunDetailPage';
import { RunningJobsPage } from '@/pages/RunningJobsPage';
import { SchedulerPage } from '@/pages/SchedulerPage';
import { DatasetsPage } from '@/pages/DatasetsPage';
import { StoragePage } from '@/pages/StoragePage';
import { SettingsPage } from '@/pages/SettingsPage';
import { BugReportsPage } from '@/pages/BugReportsPage';
import { BugReportDetailPage } from '@/pages/BugReportDetailPage';
import { BillingPage } from '@/pages/BillingPage';
import { PlatformUsersPage } from '@/pages/PlatformUsersPage';
import { PlansAdminPage } from '@/pages/PlansAdminPage';
import { AddOnsAdminPage } from '@/pages/AddOnsAdminPage';
import { CouponsAdminPage } from '@/pages/CouponsAdminPage';
import { TransactionsAdminPage } from '@/pages/TransactionsAdminPage';
import { SiteContentPage } from '@/pages/SiteContentPage';
import SettingsSitePage from '@/pages/SettingsSitePage';
import CustomersAdminPage from '@/pages/CustomersAdminPage';
import ProxiesAdminPage from '@/pages/ProxiesAdminPage';
import ReportsAdminPage from '@/pages/ReportsAdminPage';
import AuditAdminPage from '@/pages/AuditAdminPage';
import { DevTemplatesPage } from '@/pages/DevTemplatesPage';
import { AnalisaPage } from '@/pages/AnalisaPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Pengalihan yang tidak membuang query string.
 *
 * `<Navigate to="/x" />` biasa MEMBUANG `?token=...`. Untuk alamat yang
 * gunanya cuma membawa token, itu berarti pengalihannya tetap gagal, hanya di
 * halaman yang berbeda dan dengan sebab yang lebih sulit ditebak.
 */
const PindahBawaQuery = ({ ke }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${ke}${search}${hash}`} replace />;
};

export const App = () => {
  const { status, bootstrap, clearSession } = useAuthStore();
  const initTheme = useThemeStore((s) => s.init);

  useEffect(() => initTheme(), [initTheme]);

  useEffect(() => {
    // Saat refresh token ikut kedaluwarsa, klien API memanggil ini supaya
    // aplikasi kembali ke keadaan tamu alih-alih menampilkan layar kosong.
    onUnauthorized(clearSession);
    bootstrap();
  }, [bootstrap, clearSession]);

  if (status === 'idle' || status === 'loading') {
    return <PageLoader label="Menyiapkan sesi…" />;
  }

  return (
    <Routes>
      {/* Halaman publik */}
      <Route path="/" element={<PublicOnly><LandingPage /></PublicOnly>} />
      <Route path="/masuk" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
      <Route path="/daftar" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />
      <Route path="/lupa-sandi" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      {/* Halaman pemasaran terbuka untuk siapa pun, termasuk yang sudah masuk. */}
      <Route path="/harga" element={<HargaPage />} />
      <Route path="/fitur" element={<FiturPage />} />
      <Route path="/hubungi-kami" element={<KontakPage />} />
      {/* Sengaja publik: calon pengguna harus bisa menilai apakah Crawel
          cocok sebelum membuat akun. */}
      <Route path="/dokumentasi" element={<DokumentasiPage />} />
      {/* Alamat yang didaftarkan sebagai returnUrl di Duitku. Publik,
          karena pengguna kembali dari situs penyedia dan sesinya bisa
          saja belum terbaca ulang saat halaman ini terbuka. */}
      <Route path="/callback/successpayment" element={<PaymentSuccessPage />} />
      {/* Dibuka dari tautan di email, sering di peramban lain, jadi
          sengaja tidak menuntut sesi. */}
      <Route path="/verifikasi-email" element={<VerifikasiEmailPage />} />
      <Route path="/periksa-email" element={<PeriksaEmailPage />} />
      {/* Setel ulang tidak dibungkus PublicOnly: tautannya sah dibuka
          walau peramban masih memegang sesi lama. */}
      <Route path="/reset-sandi" element={<ResetPasswordPage />} />

      {/* Alamat lama yang sempat tertulis di email.

          Email yang sudah terlanjur terkirim menunjuk ke sini, dan orang yang
          membukanya sedang terkunci di luar akunnya sendiri. Membiarkannya
          jatuh ke halaman "tidak ditemukan" berarti ia harus meminta tautan
          baru tanpa tahu kenapa yang lama gagal.

          `Navigate` membawa serta query stringnya, jadi tokennya tidak hilang
          dalam perpindahan. Tanpa itu pengalihannya tetap berujung gagal,
          hanya di halaman yang berbeda. */}
      <Route path="/reset-password" element={<PindahBawaQuery ke="/reset-sandi" />} />
      <Route path="/lupa-password" element={<PindahBawaQuery ke="/lupa-sandi" />} />

      {/* Halaman yang membutuhkan sesi */}
      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/jobs" element={<JobListPage />} />
        {/* `new` dan `running` didaftarkan sebelum `:id` supaya tidak
            terbaca sebagai ID job. */}
        <Route path="/jobs/new" element={<JobCreatePage />} />
        <Route path="/jobs/running" element={<RunningJobsPage />} />
        <Route path="/jobs/:id" element={<JobDetailPage />} />
        <Route path="/jobs/:id/runs/:runId" element={<RunDetailPage />} />
        <Route path="/scheduler" element={<SchedulerPage />} />
        <Route path="/datasets" element={<DatasetsPage />} />
        <Route path="/storage" element={<StoragePage />} />
        {/* Tiap kategori punya jalurnya sendiri supaya menu sidebar bisa
            menandai yang sedang aktif, dan tautannya bisa dibagikan. */}
        <Route path="/analisa" element={<Navigate to="/analisa/eda" replace />} />
        <Route path="/analisa/:kategori" element={<AnalisaPage />} />
        <Route path="/lapor-bug" element={<BugReportsPage />} />
        <Route path="/lapor-bug/:id" element={<BugReportDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/billing" element={<BillingPage />} />

        {/* Wilayah platform. Penjaga sesungguhnya ada di server; pembungkus
            ini hanya mencegah user tersasar ke halaman yang pasti 403. */}
        <Route
          path="/manajemen/pengguna"
          element={<RequirePlatform roles={['admin']}><PlatformUsersPage /></RequirePlatform>}
        />
        <Route
          path="/manajemen/harga"
          element={<RequirePlatform roles={['admin']}><PlansAdminPage /></RequirePlatform>}
        />
        <Route
          path="/manajemen/addon"
          element={
            <RequirePlatform roles={['admin', 'root']}>
              <AddOnsAdminPage />
            </RequirePlatform>
          }
        />
        <Route
          path="/manajemen/diskon"
          element={
            <RequirePlatform roles={['admin', 'root']}>
              <CouponsAdminPage />
            </RequirePlatform>
          }
        />
        <Route
          path="/manajemen/transaksi"
          element={
            <RequirePlatform roles={['admin', 'root']}>
              <TransactionsAdminPage />
            </RequirePlatform>
          }
        />
        <Route
          path="/manajemen/konten"
          element={<RequirePlatform roles={['admin']}><SiteContentPage /></RequirePlatform>}
        />
        <Route
          path="/manajemen/pelanggan"
          element={
            <RequirePlatform roles={['admin', 'root']}>
              <CustomersAdminPage />
            </RequirePlatform>
          }
        />
        <Route
          path="/manajemen/laporan"
          element={
            <RequirePlatform roles={['admin', 'root']}>
              <ReportsAdminPage />
            </RequirePlatform>
          }
        />
        <Route
          path="/manajemen/audit"
          element={
            <RequirePlatform roles={['admin', 'root']}>
              <AuditAdminPage />
            </RequirePlatform>
          }
        />
        <Route
          path="/manajemen/proxy"
          element={
            <RequirePlatform roles={['admin', 'dev', 'root']}>
              <ProxiesAdminPage />
            </RequirePlatform>
          }
        />
        <Route
          path="/manajemen/pengaturan"
          element={
            <RequirePlatform roles={['admin', 'root']}>
              <SettingsSitePage />
            </RequirePlatform>
          }
        />
        <Route
          path="/dev/templates"
          element={<RequirePlatform roles={['dev']}><DevTemplatesPage /></RequirePlatform>}
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

/** Menahan halaman privat sampai sesi terverifikasi. */
const RequireAuth = ({ children }) => {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status !== 'authenticated') {
    // Tujuan semula disimpan supaya pengguna kembali ke tempat yang dimaksud
    // setelah berhasil masuk.
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/masuk?redirect=${redirect}`} replace />;
  }

  return children;
};

/** Pengguna yang sudah masuk tidak perlu melihat halaman login lagi. */
const RequirePlatform = ({ roles, children }) => {
  const boleh = useIsPlatform(...roles);
  if (!boleh) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const status = useAuthStore((s) => s.status);
  if (status === 'authenticated') return <Navigate to="/dashboard" replace />;
  return children;
};
